import React, { useState } from 'react';
import { FaDumbbell, FaFire, FaTrophy, FaYoutube } from 'react-icons/fa';

const WorkoutPlanGenerator = ({ YOUTUBE_CHANNEL }) => {
  const [planData, setPlanData] = useState({
    goal: '',
    experience: '',
    daysPerWeek: '3'
  });
  const [generatedPlan, setGeneratedPlan] = useState(null);

  const workoutPlans = {
    weightLoss: {
      beginner: {
        name: 'Fat Burn Starter',
        description: 'High-rep, moderate weight workouts with cardio focus',
        weeks: 4,
        schedule: {
          3: ['Full Body Cardio', 'Upper Body + HIIT', 'Lower Body + Core'],
          4: ['Upper Body + Cardio', 'Lower Body + HIIT', 'Full Body', 'Core + Cardio'],
          5: ['Chest & Cardio', 'Legs & Core', 'Back & HIIT', 'Shoulders & Abs', 'Full Body Burn']
        }
      },
      intermediate: {
        name: 'Advanced Fat Shredder',
        description: 'Intense circuit training with progressive overload',
        weeks: 6,
        schedule: {
          3: ['Push + HIIT', 'Pull + Cardio', 'Legs + Core Blast'],
          4: ['Chest & Triceps + HIIT', 'Back & Biceps + Cardio', 'Legs & Glutes', 'Shoulders & Core'],
          5: ['Push Power', 'Pull Strength', 'Leg Day', 'HIIT Cardio', 'Full Body Circuit']
        }
      }
    },
    muscleGain: {
      beginner: {
        name: 'Muscle Builder Foundation',
        description: 'Focus on compound movements and progressive overload',
        weeks: 8,
        schedule: {
          3: ['Push (Chest, Shoulders, Triceps)', 'Pull (Back, Biceps)', 'Legs (Quads, Hamstrings, Calves)'],
          4: ['Chest & Triceps', 'Back & Biceps', 'Legs & Core', 'Shoulders & Arms'],
          5: ['Chest Focus', 'Back Focus', 'Leg Day', 'Shoulder & Arms', 'Full Body Power']
        }
      },
      intermediate: {
        name: 'Mass Gain Protocol',
        description: 'Advanced split with volume training',
        weeks: 12,
        schedule: {
          3: ['Push Heavy', 'Pull Heavy', 'Legs Heavy'],
          4: ['Chest & Triceps', 'Back & Biceps', 'Legs', 'Shoulders & Core'],
          5: ['Chest', 'Back', 'Legs', 'Shoulders', 'Arms & Core']
        }
      }
    },
    maintenance: {
      beginner: {
        name: 'Balanced Fitness',
        description: 'Maintain strength and cardiovascular health',
        weeks: 4,
        schedule: {
          3: ['Full Body Strength', 'Cardio & Core', 'Total Body Workout'],
          4: ['Upper Body', 'Lower Body', 'Cardio Mix', 'Full Body'],
          5: ['Push', 'Pull', 'Legs', 'Cardio', 'Active Recovery']
        }
      },
      intermediate: {
        name: 'Performance Maintenance',
        description: 'Sustain fitness with varied training',
        weeks: 6,
        schedule: {
          3: ['Strength Training', 'Cardio & Flexibility', 'Power & Core'],
          4: ['Upper Strength', 'Lower Strength', 'HIIT', 'Full Body'],
          5: ['Push', 'Pull', 'Legs', 'Cardio', 'Functional Training']
        }
      }
    }
  };

  const exerciseDetails = {
    'Full Body Cardio': ['Jumping Jacks', 'Burpees', 'Mountain Climbers', 'High Knees', 'Squats'],
    'Upper Body + HIIT': ['Push-ups', 'Dips', 'Plank', 'Burpees', 'Shoulder Taps'],
    'Lower Body + Core': ['Squats', 'Lunges', 'Glute Bridges', 'Leg Raises', 'Plank'],
    'Push (Chest, Shoulders, Triceps)': ['Bench Press', 'Shoulder Press', 'Tricep Dips', 'Push-ups', 'Lateral Raises'],
    'Pull (Back, Biceps)': ['Pull-ups', 'Rows', 'Bicep Curls', 'Face Pulls', 'Hammer Curls'],
    'Legs (Quads, Hamstrings, Calves)': ['Squats', 'Deadlifts', 'Lunges', 'Leg Press', 'Calf Raises'],
    'Chest & Triceps': ['Bench Press', 'Incline Press', 'Dips', 'Tricep Extensions', 'Cable Flyes'],
    'Back & Biceps': ['Deadlifts', 'Pull-ups', 'Rows', 'Bicep Curls', 'Lat Pulldowns'],
    'Shoulders & Arms': ['Shoulder Press', 'Lateral Raises', 'Front Raises', 'Bicep Curls', 'Tricep Extensions'],
    'HIIT Cardio': ['Sprint Intervals', 'Jump Rope', 'Burpees', 'Box Jumps', 'Battle Ropes'],
    'Full Body Circuit': ['Squats', 'Push-ups', 'Lunges', 'Rows', 'Plank']
  };

  const generatePlan = () => {
    if (!planData.goal || !planData.experience) {
      alert('Please select both goal and experience level!');
      return;
    }

    const plan = workoutPlans[planData.goal][planData.experience];
    const schedule = plan.schedule[planData.daysPerWeek];
    
    const detailedSchedule = schedule.map((day, index) => ({
      day: index + 1,
      name: day,
      exercises: exerciseDetails[day] || ['Custom workout - Check YouTube for videos!']
    }));

    setGeneratedPlan({
      ...plan,
      schedule: detailedSchedule,
      daysPerWeek: planData.daysPerWeek
    });
  };

  const handleInputChange = (e) => {
    setPlanData({
      ...planData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-600 rounded-2xl p-1 shadow-2xl">
        <div className="bg-black rounded-2xl p-6 sm:p-8 border-2 border-yellow-500/30">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="bg-gradient-to-r from-yellow-500 to-orange-600 p-4 rounded-full border-4 border-yellow-400/50">
                <FaDumbbell className="text-black text-4xl" />
              </div>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-yellow-400 mb-3 uppercase tracking-wider">
              🏋️ Workout Plan Generator
            </h2>
            <p className="text-gray-300 text-lg">
              Get your personalized workout plan based on your goals and experience!
            </p>
          </div>

          {/* Input Form */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Goal Selection */}
            <div>
              <label className="block text-yellow-400 font-bold mb-3 text-lg uppercase tracking-wide">
                🎯 Your Goal
              </label>
              <select
                name="goal"
                value={planData.goal}
                onChange={handleInputChange}
                className="w-full px-4 py-4 bg-gray-900 border-2 border-yellow-500/50 rounded-lg text-white focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all text-base"
              >
                <option value="" className="bg-gray-900">Select Your Goal</option>
                <option value="weightLoss" className="bg-gray-900">🔥 Weight Loss</option>
                <option value="muscleGain" className="bg-gray-900">💪 Muscle Gain</option>
                <option value="maintenance" className="bg-gray-900">⚖️ Maintenance</option>
              </select>
            </div>

            {/* Experience Level */}
            <div>
              <label className="block text-orange-400 font-bold mb-3 text-lg uppercase tracking-wide">
                📊 Experience Level
              </label>
              <select
                name="experience"
                value={planData.experience}
                onChange={handleInputChange}
                className="w-full px-4 py-4 bg-gray-900 border-2 border-orange-500/50 rounded-lg text-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400/20 transition-all text-base"
              >
                <option value="" className="bg-gray-900">Select Experience</option>
                <option value="beginner" className="bg-gray-900">🌱 Beginner</option>
                <option value="intermediate" className="bg-gray-900">🔥 Intermediate</option>
              </select>
            </div>

            {/* Days Per Week */}
            <div>
              <label className="block text-red-400 font-bold mb-3 text-lg uppercase tracking-wide">
                📅 Days Per Week
              </label>
              <select
                name="daysPerWeek"
                value={planData.daysPerWeek}
                onChange={handleInputChange}
                className="w-full px-4 py-4 bg-gray-900 border-2 border-red-500/50 rounded-lg text-white focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-400/20 transition-all text-base"
              >
                <option value="3" className="bg-gray-800">3 Days</option>
                <option value="4" className="bg-gray-800">4 Days</option>
                <option value="5" className="bg-gray-800">5 Days</option>
              </select>
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={generatePlan}
            className="w-full bg-gradient-to-r from-yellow-500 via-orange-500 to-red-600 hover:from-yellow-600 hover:via-orange-600 hover:to-red-700 text-black font-bold py-5 rounded-lg transition-all transform hover:scale-105 shadow-2xl text-lg flex items-center justify-center space-x-3 border-2 border-yellow-400 uppercase tracking-wider"
          >
            <FaFire className="text-2xl" />
            <span>Generate My Workout Plan</span>
            <FaTrophy className="text-2xl" />
          </button>
        </div>
      </div>

      {/* Generated Plan Display */}
      {generatedPlan && (
        <div className="space-y-6">
          {/* Plan Overview */}
          <div className="bg-gradient-to-r from-yellow-500 to-orange-600 rounded-2xl p-8 text-black shadow-2xl border-4 border-yellow-400/50">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-3xl font-bold mb-2 uppercase tracking-wide">{generatedPlan.name}</h3>
                <p className="text-gray-900 text-lg font-semibold">{generatedPlan.description}</p>
              </div>
              <div className="bg-black/80 backdrop-blur-sm px-6 py-4 rounded-xl text-center border-2 border-yellow-400">
                <div className="text-3xl font-bold text-yellow-400">{generatedPlan.weeks}</div>
                <div className="text-sm text-white uppercase">Weeks</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-black/80 backdrop-blur-sm p-4 rounded-xl text-center border-2 border-yellow-400/50">
                <div className="text-2xl mb-2">🎯</div>
                <div className="font-bold text-yellow-400 uppercase">Goal</div>
                <div className="text-sm text-white capitalize">{planData.goal.replace(/([A-Z])/g, ' $1')}</div>
              </div>
              <div className="bg-black/80 backdrop-blur-sm p-4 rounded-xl text-center border-2 border-orange-400/50">
                <div className="text-2xl mb-2">📊</div>
                <div className="font-bold text-orange-400 uppercase">Level</div>
                <div className="text-sm text-white capitalize">{planData.experience}</div>
              </div>
              <div className="bg-black/80 backdrop-blur-sm p-4 rounded-xl text-center border-2 border-red-400/50">
                <div className="text-2xl mb-2">📅</div>
                <div className="font-bold text-red-400 uppercase">Frequency</div>
                <div className="text-sm text-white">{generatedPlan.daysPerWeek} Days/Week</div>
              </div>
            </div>
          </div>

          {/* Weekly Schedule */}
          <div className="bg-gray-900 rounded-2xl p-8 border-2 border-yellow-500/30 shadow-2xl">
            <h3 className="text-2xl font-bold text-yellow-400 mb-6 flex items-center uppercase tracking-wide">
              <FaDumbbell className="mr-3" />
              Your Weekly Workout Schedule
            </h3>

            <div className="grid grid-cols-1 gap-4">
              {generatedPlan.schedule.map((workout, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-2 border-yellow-500/30 rounded-lg p-6 hover:border-yellow-400 transition-all"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="bg-gradient-to-r from-yellow-500 to-orange-600 text-black w-12 h-12 rounded-lg flex items-center justify-center font-bold text-xl border-2 border-yellow-400">
                          {workout.day}
                        </div>
                        <h4 className="text-xl font-bold text-yellow-400 uppercase tracking-wide">{workout.name}</h4>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mb-3">
                        {workout.exercises.map((exercise, idx) => (
                          <span
                            key={idx}
                            className="bg-gray-800 text-yellow-400 px-3 py-1 rounded-lg text-sm border border-yellow-500/30 font-semibold"
                          >
                            {exercise}
                          </span>
                        ))}
                      </div>
                    </div>

                    <a
                      href={YOUTUBE_CHANNEL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-bold transition-all flex items-center justify-center space-x-2 whitespace-nowrap border-2 border-red-500 uppercase"
                    >
                      <FaYoutube />
                      <span>Watch Video</span>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tips & Recommendations */}
          <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl p-8 text-white shadow-2xl border-4 border-orange-400/50">
            <h3 className="text-2xl font-bold mb-6 flex items-center uppercase tracking-wide">
              <FaTrophy className="mr-3 text-yellow-400" />
              Pro Tips for Success
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3 bg-black/30 p-4 rounded-lg border border-yellow-500/30">
                  <div className="bg-yellow-500 p-2 rounded-lg flex-shrink-0">
                    <span className="text-xl">💪</span>
                  </div>
                  <div>
                    <h4 className="font-bold mb-1 text-yellow-400 uppercase">Progressive Overload</h4>
                    <p className="text-gray-200 text-sm">Gradually increase weight or reps each week</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 bg-black/30 p-4 rounded-lg border border-orange-500/30">
                  <div className="bg-orange-500 p-2 rounded-lg flex-shrink-0">
                    <span className="text-xl">🥗</span>
                  </div>
                  <div>
                    <h4 className="font-bold mb-1 text-orange-300 uppercase">Nutrition Matters</h4>
                    <p className="text-gray-200 text-sm">Eat according to your goal - surplus for gain, deficit for loss</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 bg-black/30 p-4 rounded-lg border border-red-500/30">
                  <div className="bg-red-500 p-2 rounded-lg flex-shrink-0">
                    <span className="text-xl">😴</span>
                  </div>
                  <div>
                    <h4 className="font-bold mb-1 text-red-300 uppercase">Rest & Recovery</h4>
                    <p className="text-gray-200 text-sm">Get 7-9 hours of sleep for optimal muscle recovery</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start space-x-3 bg-black/30 p-4 rounded-lg border border-yellow-500/30">
                  <div className="bg-yellow-500 p-2 rounded-lg flex-shrink-0">
                    <span className="text-xl">💧</span>
                  </div>
                  <div>
                    <h4 className="font-bold mb-1 text-yellow-400 uppercase">Stay Hydrated</h4>
                    <p className="text-gray-200 text-sm">Drink at least 3-4 liters of water daily</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 bg-black/30 p-4 rounded-lg border border-orange-500/30">
                  <div className="bg-orange-500 p-2 rounded-lg flex-shrink-0">
                    <span className="text-xl">📹</span>
                  </div>
                  <div>
                    <h4 className="font-bold mb-1 text-orange-300 uppercase">Follow Along Videos</h4>
                    <p className="text-gray-200 text-sm">Watch our YouTube channel for detailed exercise demos</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 bg-black/30 p-4 rounded-lg border border-red-500/30">
                  <div className="bg-red-500 p-2 rounded-lg flex-shrink-0">
                    <span className="text-xl">📊</span>
                  </div>
                  <div>
                    <h4 className="font-bold mb-1 text-red-300 uppercase">Track Progress</h4>
                    <p className="text-gray-200 text-sm">Take photos and measurements every 2 weeks</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* YouTube CTA */}
          <div className="bg-gradient-to-r from-red-600 to-red-800 rounded-2xl p-8 text-center shadow-2xl border-4 border-red-500/50">
            <h3 className="text-2xl font-bold text-white mb-4 uppercase tracking-wide">
              🎥 Watch Detailed Workout Videos
            </h3>
            <p className="text-red-100 mb-6 max-w-2xl mx-auto">
              Subscribe to Rugged Fitness Coach Sabari's YouTube channel for step-by-step workout tutorials, 
              form tips, and motivation to crush your fitness goals!
            </p>
            <a
              href={YOUTUBE_CHANNEL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-3 bg-white text-red-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg border-2 border-white uppercase tracking-wide"
            >
              <FaYoutube size={28} />
              <span>Subscribe Now</span>
            </a>
          </div>
        </div>
      )}

      {/* Welcome Message */}
      {!generatedPlan && (
        <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-600 rounded-2xl p-1 shadow-2xl">
          <div className="bg-black rounded-2xl p-8 text-white text-center border-2 border-yellow-500/30">
            <div className="text-6xl mb-4">🏋️‍♂️</div>
            <h3 className="text-2xl font-bold mb-4 text-yellow-400 uppercase tracking-wide">Ready to Transform Your Body?</h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Select your fitness goal, experience level, and training frequency above to generate 
              your personalized workout plan. Let's build your dream physique together!
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-900 border-2 border-yellow-500/50 p-4 rounded-xl hover:border-yellow-400 transition-all">
                <div className="text-3xl mb-2">🔥</div>
                <p className="font-bold text-yellow-400 uppercase">Weight Loss</p>
                <p className="text-sm text-gray-400">Burn fat & get lean</p>
              </div>
              <div className="bg-gray-900 border-2 border-orange-500/50 p-4 rounded-xl hover:border-orange-400 transition-all">
                <div className="text-3xl mb-2">💪</div>
                <p className="font-bold text-orange-400 uppercase">Muscle Gain</p>
                <p className="text-sm text-gray-400">Build strength & size</p>
              </div>
              <div className="bg-gray-900 border-2 border-red-500/50 p-4 rounded-xl hover:border-red-400 transition-all">
                <div className="text-3xl mb-2">⚖️</div>
                <p className="font-bold text-red-400 uppercase">Maintenance</p>
                <p className="text-sm text-gray-400">Stay fit & healthy</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkoutPlanGenerator;
